/**
 * The frame every redesigned modal sits in (CAE-1547): `.m-modal` and
 * `.m-modal-host` in the prototype's modals.css, with the regions of
 * `ModalShell` from the handoff's `shared/modals-shared.jsx`.
 *
 * ```
 * head  ← context chip, title, subtitle, close
 * body  ← the only region that scrolls
 * foot  ← summary on the left, actions on the right
 * ```
 *
 * Built on MUI's `Dialog`, which provides the focus trap, the return of focus
 * to the trigger, body scroll locking, the portal and Escape. Those are easy to
 * get subtly wrong by hand, and `Popover` in this kit is deliberately *not*
 * modal. This file adds the regions, the context chip and the visual spec.
 *
 * Its props mirror the prototype's `ModalShell` rather than a compound
 * `<Head>/<Body>/<Foot>`, because the shell has to know which regions exist:
 * the subtitle wires `aria-describedby`, and a modal with no body (the
 * delete-tier confirm) drops the head's hairline.
 *
 * > **The stylesheet wins over 08-modals/README.md**, as it has throughout the
 * > epic. The README and CAE-1547's description give 16px radius, 24px side
 * > padding, a `rgba(13,14,18,.42)` scrim, a different shadow, an 880px height
 * > cap, ≈760/≈920 widths and a centred panel. modals.css draws 18px, 28px, the
 * > `--ink`-at-40% scrim with a 2px blur, `--shadow-modal`, `100vh - 64px`, no
 * > fixed widths (the pickers fill the host, the confirms are 440 and 460) and
 * > a top-aligned panel. The README's 120ms fade is kept, because the CSS has
 * > no transition at all to disagree with it.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { CloseIcon } from './line-icons';

/** README "fades in 120ms". MUI's default of 225ms reads sluggish on a picker. */
const FADE_MS = 120;

/**
 * `.m-modal-host-content{max-width:1440px}`. The Add Fixtures and Add Outcomes
 * modals are `width:100%` inside it, so on most screens they fill the viewport
 * less the host's 32px inset. Confirms pass their own width.
 */
const HOST_MAX_WIDTH = 1440;

/** `.m-modal-host{padding:32px}`, which is also why max-height is `100vh - 64px`. */
const HOST_INSET = 32;

/** `.m-modal-head` / `-body` / `-foot` side padding. The README's 24 is wrong. */
const SIDE_PADDING = 28;

/** `.m-modal-body-split{grid-template-columns:280px 1fr}` — the filter sidebar. */
const ASIDE_WIDTH = 280;

/**
 * `@media (max-width:960px)` in modals.css: under this the sidebar stops being
 * a column and becomes a band above the list, so the filters still arrive
 * first without squeezing the rows into nothing.
 */
const ASIDE_COLLAPSE_AT = 960;

/** `.m-eyebrow-id`. A small square-ish tag, not the two-tone `IdChip`. */
const CONTEXT_ID_RADIUS = 5;

/**
 * What can take focus inside the panel. Used to put focus on the first control
 * when the modal opens; MUI's trap on its own focuses the panel itself.
 */
const FOCUSABLE = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface ModalHostProps {
  open: boolean;
  /**
   * Called for Escape, backdrop click and the close button. It is the only
   * callback the shell fires itself, so a dismissal can never confirm anything.
   * The confirming action belongs to the caller's own button in `actions`.
   */
  onClose: () => void;
  title: ReactNode;
  /** The helper line under the title. Becomes the dialog's description. */
  subtitle?: ReactNode;
  /**
   * The line above the title that says which contest (or reel, or tier) the
   * modal acts on. Usually a `ModalContext`. Carry it into every modal that
   * edits contest-scoped data.
   */
  context?: ReactNode;
  /** Panel width cap in px. Defaults to the host's 1440, i.e. fill the viewport. */
  maxWidth?: number;
  /** The foot's left slot. Free-form: a `ModalFootSummary`, a validation line, anything. */
  summary?: ReactNode;
  /** The foot's right slot, usually a ghost Cancel and the primary action. */
  actions?: ReactNode;
  /**
   * Drops the body's padding, for content that lays out its own: a sticky
   * filter strip, or a list running edge to edge. modals.css itself pads
   * nothing here; 20px 28px is the default because most bodies are plain forms.
   */
  flush?: boolean;
  /**
   * The filter sidebar beside the body (`.m-modal-body-split` /
   * `.m-modal-side`), as the prototype's `ModalShell` takes it.
   *
   * It belongs to the shell rather than to `children` because the split is a
   * change to how the *body region* scrolls, not something drawn inside it: the
   * two panes each scroll on their own while the head and foot stay pinned. A
   * caller laying this out inside `children` would be fighting the body's own
   * single scroller.
   *
   * `flush` applies to the content pane only — the sidebar always brings the
   * stylesheet's own padding.
   */
  aside?: ReactNode;
  /** The body. Leave it out for a title-and-buttons confirm. */
  children?: ReactNode;
}

export const ModalHost = ({
  open,
  onClose,
  title,
  subtitle,
  context,
  maxWidth = HOST_MAX_WIDTH,
  summary,
  actions,
  flush = false,
  aside,
  children,
}: ModalHostProps) => {
  const titleId = useId();
  const subtitleId = useId();
  const hasBody = children != null && children !== false;
  const hasFoot = summary != null || actions != null;

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      fullWidth
      maxWidth={false}
      scroll="paper"
      transitionDuration={FADE_MS}
      aria-labelledby={titleId}
      aria-describedby={subtitle != null ? subtitleId : undefined}
      sx={{
        // `.m-modal-host{align-items:flex-start}`. Top-aligned so a picker
        // whose list grows or shrinks under a filter doesn't move its head.
        '& .MuiDialog-container': { alignItems: 'flex-start' },
      }}
      slotProps={{
        backdrop: {
          sx: (theme: Theme) => ({
            backgroundColor: theme.tokens.componentColor.modalScrim,
            backdropFilter: 'blur(2px)',
          }),
        },
        paper: {
          sx: (theme: Theme) => ({
            maxWidth: `${maxWidth}px`,
            width: `calc(100% - ${HOST_INSET * 2}px)`,
            maxHeight: `calc(100% - ${HOST_INSET * 2}px)`,
            m: `${HOST_INSET}px`,
            borderRadius: `${theme.tokens.radius.frame}px`,
            border: theme.tokens.border.default,
            backgroundColor: theme.tokens.color.surface,
            boxShadow: theme.tokens.shadow.modal,
            overflow: 'hidden',
          }),
        },
      }}
    >
      <ModalPanel>
        <ModalHead
          titleId={titleId}
          subtitleId={subtitleId}
          title={title}
          subtitle={subtitle}
          context={context}
          onClose={onClose}
          divider={hasBody}
        />
        {hasBody &&
          (aside != null ? (
            <ModalSplitBody aside={aside} flush={flush}>
              {children}
            </ModalSplitBody>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                padding: flush ? 0 : `20px ${SIDE_PADDING}px`,
              }}
            >
              {children}
            </Box>
          ))}
        {hasFoot && (
          <Box
            sx={(theme: Theme) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexShrink: 0,
              padding: `16px ${SIDE_PADDING}px`,
              borderTop: theme.tokens.border.default,
              backgroundColor: theme.tokens.color.surfaceMuted,
            })}
          >
            <Box sx={{ minWidth: 0 }}>{summary}</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {actions}
            </Box>
          </Box>
        )}
      </ModalPanel>
    </Dialog>
  );
};

/**
 * `.m-modal-body-split` — the filter sidebar and the content beside it, each
 * its own scroller under a body that no longer scrolls at all.
 *
 * The sidebar is an `<aside>`: it is the filters for the list next to it, and
 * naming it in the markup gives a screen-reader user a landmark to skip rather
 * than seven form controls to tab past on the way to the rows.
 */
const ModalSplitBody = ({
  aside,
  flush,
  children,
}: {
  aside: ReactNode;
  flush: boolean;
  children: ReactNode;
}) => (
  <Box
    sx={{
      flex: 1,
      minHeight: 0,
      display: 'grid',
      overflow: 'hidden',
      gridTemplateColumns: `${ASIDE_WIDTH}px 1fr`,
      [`@media (max-width:${ASIDE_COLLAPSE_AT}px)`]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto 1fr',
      },
    }}
  >
    <Box
      component="aside"
      sx={(theme: Theme) => ({
        minWidth: 0,
        overflowY: 'auto',
        padding: '18px 18px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        backgroundColor: theme.tokens.color.surfaceMuted,
        borderRight: theme.tokens.border.default,
        [`@media (max-width:${ASIDE_COLLAPSE_AT}px)`]: {
          borderRight: 'none',
          borderBottom: theme.tokens.border.default,
        },
      })}
    >
      {aside}
    </Box>
    <Box
      sx={{
        minWidth: 0,
        minHeight: 0,
        overflowY: 'auto',
        padding: flush ? 0 : `20px ${SIDE_PADDING}px`,
      }}
    >
      {children}
    </Box>
  </Box>
);

/**
 * The column the three regions stack in, and the hook that moves focus to the
 * first control on open.
 *
 * Mounted only while the dialog is open, so the effect runs once per opening.
 * It runs before MUI's trap's own effect (children's effects fire first), and
 * the trap only focuses the panel when focus is not already inside it, so the
 * two don't fight. The trap still records the trigger and hands focus back to
 * it on close.
 */
const ModalPanel = ({ children }: { children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
  }, []);

  return (
    <Box ref={ref} sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {children}
    </Box>
  );
};

interface ModalHeadProps {
  titleId: string;
  subtitleId: string;
  title: ReactNode;
  subtitle?: ReactNode;
  context?: ReactNode;
  onClose: () => void;
  /**
   * The hairline under the head. Off when there is no body:
   * `.prz-confirm-modal .m-modal-head{border-bottom:0;padding-bottom:0}`, so a
   * confirm reads as one block sitting on its foot.
   */
  divider: boolean;
}

const ModalHead = ({
  titleId,
  subtitleId,
  title,
  subtitle,
  context,
  onClose,
  divider,
}: ModalHeadProps) => (
  <Box
    sx={(theme: Theme) => ({
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 2,
      flexShrink: 0,
      padding: `24px ${SIDE_PADDING}px ${divider ? 18 : 0}px`,
      borderBottom: divider ? theme.tokens.border.default : 'none',
    })}
  >
    <Box sx={{ minWidth: 0 }}>
      {context != null && <Box sx={{ mb: 1 }}>{context}</Box>}
      <Typography
        id={titleId}
        variant="h2"
        component="h2"
        sx={(theme: Theme) => ({ m: 0, color: theme.tokens.color.accentStrong })}
      >
        {title}
      </Typography>
      {subtitle != null && (
        <Typography
          id={subtitleId}
          component="p"
          sx={(theme: Theme) => ({
            m: '6px 0 0',
            maxWidth: '680px',
            fontSize: '13.5px',
            lineHeight: 1.45,
            color: theme.tokens.color.ink3,
          })}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
    {/* `.m-iconbtn` */}
    <IconButton
      aria-label="Close"
      onClick={onClose}
      disableRipple
      sx={(theme: Theme) => ({
        flexShrink: 0,
        width: '32px',
        height: '32px',
        borderRadius: `${theme.tokens.radius.control}px`,
        border: `${theme.tokens.border.width} solid transparent`,
        color: theme.tokens.color.ink3,
        transition: 'background-color .12s, border-color .12s',
        '&:hover': {
          backgroundColor: theme.tokens.color.bg,
          borderColor: theme.tokens.color.line,
        },
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.tokens.color.accent}`,
          outlineOffset: '2px',
        },
      })}
    >
      <CloseIcon sx={{ fontSize: '16px' }} />
    </IconButton>
  </Box>
);

interface ModalContextProps {
  /** What the modal is doing, e.g. "Adding to", "Editing tier". */
  lead: ReactNode;
  /** The stable id of the thing being acted on, e.g. `CT-58291` or `Reel 1`. */
  id: string;
  /** Its human name. Optional: "New tier · CT-58291" has no name to show. */
  name?: ReactNode;
}

/**
 * The context chip: `.m-modal-eyebrow` + `.m-eyebrow-row`, e.g.
 * "Adding to `CT-58291` · This is the name". It keeps the operator sure of
 * which contest a picker writes into.
 *
 * The id tag is the eyebrow's own single-tone mono chip, not the two-tone
 * `IdChip` from the contest header. The README calls it "an ID chip", but the
 * stylesheet draws a different, smaller thing.
 */
export const ModalContext = ({ lead, id, name }: ModalContextProps) => (
  <Box
    sx={(theme: Theme) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      flexWrap: 'wrap',
      fontFamily: theme.tokens.font.sans,
      fontSize: '11.5px',
      fontWeight: 600,
      letterSpacing: '0.01em',
      color: theme.tokens.color.ink4,
    })}
  >
    <span>{lead}</span>
    <Box
      component="span"
      sx={(theme: Theme) => ({
        fontFamily: theme.tokens.font.mono,
        fontSize: '11px',
        color: theme.tokens.color.ink3,
        backgroundColor: theme.tokens.componentColor.idChipKeyBg,
        padding: '2px 7px',
        borderRadius: `${CONTEXT_ID_RADIUS}px`,
        border: theme.tokens.border.default,
        whiteSpace: 'nowrap',
      })}
    >
      {id}
    </Box>
    {name != null && (
      <>
        <span aria-hidden>·</span>
        <Box
          component="span"
          sx={(theme: Theme) => ({
            color: theme.tokens.color.ink3,
            fontSize: '12px',
            fontWeight: 500,
            letterSpacing: 0,
          })}
        >
          {name}
        </Box>
      </>
    )}
  </Box>
);

interface ModalFootSummaryProps {
  count: number;
  /** What was counted, already pluralised: "fixtures selected". */
  label: ReactNode;
  /** Supporting facts under the count, dot-separated: "3 markets", "7 outcomes included". */
  meta?: ReactNode[];
}

/**
 * The foot summary both pickers share (`.m-foot-summary`): a big mono count, a
 * label beside it, and a dot-separated line of supporting facts. The count
 * greys out at zero, so an empty selection reads as "nothing yet".
 *
 * Optional. The prize-tier modal puts a preview of the tier being built in the
 * same slot, so `ModalHost`'s `summary` takes any node.
 */
export const ModalFootSummary = ({ count, label, meta }: ModalFootSummaryProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
      <Box
        component="span"
        sx={(theme: Theme) => ({
          fontFamily: theme.tokens.font.mono,
          fontSize: '20px',
          fontWeight: 600,
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
          color: count === 0 ? theme.tokens.color.ink4 : theme.tokens.color.ink,
        })}
      >
        {count}
      </Box>
      <Box
        component="span"
        sx={(theme: Theme) => ({
          fontSize: '13.5px',
          fontWeight: 500,
          color: theme.tokens.color.ink2,
        })}
      >
        {label}
      </Box>
    </Box>
    {meta && meta.length > 0 && (
      <Box
        sx={(theme: Theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: '12.5px',
          fontVariantNumeric: 'tabular-nums',
          color: theme.tokens.color.ink3,
        })}
      >
        {meta.map((fact, index) => (
          <Box key={index} component="span" sx={{ display: 'contents' }}>
            {index > 0 && (
              // `.m-dot-sep`
              <Box
                component="span"
                aria-hidden
                sx={(theme: Theme) => ({
                  width: '3px',
                  height: '3px',
                  borderRadius: '50%',
                  flexShrink: 0,
                  backgroundColor: theme.tokens.componentColor.dotSeparator,
                })}
              />
            )}
            <span>{fact}</span>
          </Box>
        ))}
      </Box>
    )}
  </Box>
);
